import React, { useCallback } from 'react'
import ReactFocusLock from 'react-focus-lock'
import { focus, getAllFocusable } from '@mote/helpers/dom'
import type { FocusableElement } from '@mote/helpers/types'

export interface FocusTrapProps {
  children: React.ReactNode
  group?: string
  initialFocusRef?: React.RefObject<FocusableElement>
  finalFocusRef?: React.RefObject<FocusableElement>
  contentRef?: React.RefObject<HTMLElement>
  restoreFocus?: boolean
  isDisabled?: boolean
  autoFocus?: boolean
  persistentFocus?: boolean
  lockFocusAcrossFrames?: boolean
}

export const FocusTrap = (props: FocusTrapProps) => {
  const {
    group,
    initialFocusRef,
    finalFocusRef,
    contentRef,
    restoreFocus,
    children,
    isDisabled,
    autoFocus,
    persistentFocus,
    lockFocusAcrossFrames
  } = props

  const onActivation = useCallback(() => {
    if (initialFocusRef?.current) {
      initialFocusRef.current.focus()
    } else if (contentRef?.current) {
      const focusables = getAllFocusable(contentRef.current)
      if (focusables.length === 0) {
        focus(contentRef.current, { nextTick: true })
      }
    }
  }, [initialFocusRef, contentRef])

  const onDeactivation = useCallback(() => {
    finalFocusRef?.current?.focus()
  }, [finalFocusRef])

  const returnFocus = restoreFocus && !finalFocusRef

  return (
    <ReactFocusLock
      group={group}
      crossFrame={lockFocusAcrossFrames}
      persistentFocus={persistentFocus}
      autoFocus={autoFocus}
      disabled={isDisabled}
      onActivation={onActivation}
      onDeactivation={onDeactivation}
      returnFocus={returnFocus}
    >
      {children}
    </ReactFocusLock>
  )
}
